import { Alert, RadioGroup, Stack, Typography } from '@mui/material';
import { MemberDeleteOptions, UserIdentityProvider, type VisitorGroup } from 'graphql/generated/graphqlTypes';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import ModalTemplate from 'v2/components/Modal/ModalTemplate';
import { DELETE_GROUP_MEMBER_ACTION, DeleteGroupMemberAction, MEMBER_ACTION_TO_DELETE_OPTIONS } from './constants';
import DeleteGroupAndUsersOption from './DeleteGroupAndUsersOption';
import DeleteGroupAndUsersWithNoOtherGroupOption from './DeleteGroupAndUsersWithNoOtherGroupOption';
import DeleteGroupOnlyOption from './DeleteGroupOnlyOption';

interface DeleteGroupModalProps {
  visitorGroup: VisitorGroup;
  handleDelete: (options: MemberDeleteOptions) => Promise<void>;
}

interface FormValues {
  memberAction: DeleteGroupMemberAction;
}

const DeleteGroupModal: React.FC<React.PropsWithChildren<DeleteGroupModalProps>> = ({ visitorGroup, handleDelete }) => {
  const { translation } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(false);
  const { control, watch } = useForm<FormValues>({
    defaultValues: { memberAction: DELETE_GROUP_MEMBER_ACTION.keep }
  });
  const memberAction = watch('memberAction');

  const membersInOtherGroupsCount = visitorGroup.members.filter(
    (member) => member.visitorGroupMembership.length > 1
  ).length;
  const membersLosingAccessCount = visitorGroup.members.length - membersInOtherGroupsCount;
  const entraMembersCount = visitorGroup.members.filter(
    (member) => member.identityProvider === UserIdentityProvider.Entra
  ).length;

  const handleClick = async () => {
    setIsLoading(true);

    try {
      await handleDelete(MEMBER_ACTION_TO_DELETE_OPTIONS[memberAction]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalTemplate
      id="delete-user-group-modal"
      title={translation.delete_group_confirmation_title.replace('{{name}}', visitorGroup.name)}
      primaryAction={{
        id: 'confirm-delete-group-button',
        label: translation.delete,
        onClick: handleClick,
        dangerzone: true,
        loading: isLoading
      }}
      secondaryAction={{
        id: 'cancel-button',
        label: translation.cancel
      }}
    >
      <Stack spacing={3}>
        <Typography>
          {translation.this_group_has_users_question.replace('{{count}}', String(visitorGroup.members.length))}
        </Typography>
        <Controller
          control={control}
          name="memberAction"
          render={({ field }) => (
            <RadioGroup {...field}>
              <Stack gap={1}>
                <DeleteGroupOnlyOption
                  isSelected={field.value === DELETE_GROUP_MEMBER_ACTION.keep}
                  membersLosingAccessCount={membersLosingAccessCount}
                  membersInOtherGroupsCount={membersInOtherGroupsCount}
                />
                <DeleteGroupAndUsersWithNoOtherGroupOption
                  isSelected={field.value === DELETE_GROUP_MEMBER_ACTION.unassign}
                  membersLosingAccessCount={membersLosingAccessCount}
                  membersInOtherGroupsCount={membersInOtherGroupsCount}
                />
                <DeleteGroupAndUsersOption
                  isSelected={field.value === DELETE_GROUP_MEMBER_ACTION.delete}
                  totalMembersCount={visitorGroup.members.length}
                />
                {field.value === DELETE_GROUP_MEMBER_ACTION.delete && (
                  <Alert severity="info" variant="standard">
                    {translation.entra_ID_users_deletion_warning.replace('{{count}}', String(entraMembersCount))}
                  </Alert>
                )}
              </Stack>
            </RadioGroup>
          )}
        />
      </Stack>
    </ModalTemplate>
  );
};

export default DeleteGroupModal;
